import React, { useEffect, useState, useCallback } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import { openAlert, openConfirm } from '../../utils/common';
import Modal from '../../components/Layout/Modal';
import { validatePassword } from '../../utils/validationUtils';
import { useSecurityMonitor } from '../../hooks/useValidation';
import { sanitizeApiRequest, sanitizeString, validateXssPrevention } from '../../utils/xssProtection';
import ErrorMessage from '../../components/UI/ErrorMessage';
import adminAccountService from '../../services/adminAccountService';

const AdminAccounts = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [allChecked, setAllChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [originalRole, setOriginalRole] = useState(''); // 권한 변경 추적용

  const [adminAccount, setAdminAccount] = useState({
    idx: '',
    id: '',
    pw: '',
    name: '',
    role: '',
    email: '',
    reg_date: '',
    isEdit: false,
  });

  // 보안 모니터링 훅
  const {
    securityLog,
    riskLevel,
    logSecurityEvent,
    clearSecurityLog,
    eventCount
  } = useSecurityMonitor();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const result = await adminAccountService.getAdmins();
      if (result.success) {
        setAdmins(result.data.admins);
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      openAlert({ title: '알림', description: '데이터 로드 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleAllChange = e => {
    const checked = e.target.checked;
    setAllChecked(checked);
    setSelectedIds(checked ? admins.map(item => item.idx) : []);
  };

  const handleSelect = idx => {
    let next = [...selectedIds];
    if (next.includes(idx)) next = next.filter(x => x !== idx);
    else next.push(idx);
    setSelectedIds(next);
    setAllChecked(next.length === admins.length);
  };

  const handleBulkDeleteConfirm = () => {
    if (selectedIds.length === 0) return openAlert({ title: '알림', description: '삭제할 계정을 선택해주세요.' });
    openConfirm({
      title: '삭제하기',
      description: '선택된 관리자를 삭제하시겠습니까?',
      positive_method: handleBulkDelete,
      positive_param: selectedIds,
      negative_method: null,
      negative_param: null,
      positive_btn: '확인',
      negative_btn: '취소',
    });
  };

  const handleBulkDelete = async ids => {
    setLoading(true);
    try {
      const result = await adminAccountService.deleteAdmins(ids);
      if (result.success) {
        openAlert({ title: '성공', description: result.message });
        setSelectedIds([]);
        setAllChecked(false);
        fetchAdmins();
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      openAlert({ title: '알림', description: '삭제 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setAdminAccount({
      idx: '',
      id: '',
      pw: '',
      name: '',
      role: '',
      email: '',
      reg_date: '',
      isEdit: false,
    });
    setOriginalRole('');
    setValidationErrors({});
    setPasswordError('');
    clearSecurityLog();
    setShowModal(true);
  };

  const handleOpenEdit = admin => {
    setAdminAccount({
      idx: admin.idx,
      id: admin.id,
      pw: '',
      name: admin.name,
      role: admin.role,
      email: admin.email,
      reg_date: admin.reg_date,
      isEdit: true,
    });
    setOriginalRole(admin.role); // 원본 권한 저장
    setValidationErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setValidationErrors({});
    setPasswordError('');
    setShowSecurityWarning(false);
    clearSecurityLog();
  };

  const handleToggleStatus = async (admin) => {
    const newStatus = admin.status !== 1;
    const actionText = newStatus ? "활성화" : "비활성화";

    openConfirm({
      title: `계정 ${actionText}`,
      description: `${admin.name}(${
        admin.id
      }) 계정을 ${actionText}하시겠습니까?${
        !newStatus ? "\n비활성화된 계정은 로그인이 차단됩니다." : ""
      }`,
      positive_method: async () => {
        setLoading(true);
        try {
          const result = await adminAccountService.setAdminStatus(
            admin.idx,
            newStatus
          );
          if (result.success) {
            openAlert({ title: "성공", description: result.message });
            fetchAdmins();
          } else {
            openAlert({ title: "알림", description: result.message });
          }
        } catch (e) {
          openAlert({
            title: "알림",
            description: `계정 ${actionText} 중 오류가 발생했습니다.`,
          });
        } finally {
          setLoading(false);
        }
      },
      positive_param: null,
      negative_method: null,
      negative_param: null,
      positive_btn: "확인",
      negative_btn: "취소",
    });
  };

  const handleCreateAdmin = async () => {
    // 1. 클라이언트 검증
    const validation = validateAdminData();
    
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      openAlert({ title: '입력 오류', description: firstError });
      return;
    }

    // 2. 보안 위험 감지 시 추가 확인
    if (eventCount > 0 && riskLevel === 'HIGH') {
      const confirmResult = window.confirm(
        '입력 데이터에서 보안 위험이 감지되었습니다. 계속 진행하시겠습니까?'
      );
      if (!confirmResult) {
        return;
      }
    }

    setLoading(true);
    try {
      // 3. API 요청 데이터 정화
      const safeAdminData = sanitizeApiRequest({
        id: adminAccount.id,
        pw: adminAccount.pw,
        name: adminAccount.name,
        role: adminAccount.role,
        email: adminAccount.email
      }, '/admin/create');
      
      // 4. 보안 이벤트 로깅
      if (eventCount > 0) {
        console.warn('Admin create attempt with security events:', {
          eventCount,
          riskLevel,
          adminId: safeAdminData.id,
          role: safeAdminData.role
        });
      }
      
      // 5. 관리자 생성 보안 로깅
      logSecurityEvent({
        type: 'ADMIN_CREATE_ATTEMPT',
        field: 'admin_management',
        riskLevel: 'HIGH',
        message: 'New admin account creation attempt',
        adminId: safeAdminData.id,
        role: safeAdminData.role
      });
      
      const result = await adminAccountService.createAdmin(safeAdminData);
      
      if (result.success) {
        // 성공 시 보안 로그 정리
        clearSecurityLog();
        openAlert({ title: '성공', description: result.message });
        handleCloseModal();
        fetchAdmins();
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      console.error('Admin create error:', e);
      
      // 오류 시 보안 이벤트 로깅
      logSecurityEvent({
        type: 'ADMIN_CREATE_ERROR',
        field: 'admin_management',
        riskLevel: 'HIGH',
        message: 'Admin creation process error',
        error: e.message
      });
      
      openAlert({ title: '알림', description: '관리자 등록 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAdmin = async () => {
    // 1. 클라이언트 검증
    const validation = validateAdminData(true);
    
    if (!validation.isValid) {
      const firstError = Object.values(validation.errors)[0];
      openAlert({ title: '입력 오류', description: firstError });
      return;
    }

    // 2. 권한 변경 감지 및 추가 확인
    const roleChanged = originalRole !== adminAccount.role;
    if (roleChanged) {
      const confirmResult = window.confirm(
        `관리자 권한을 '${originalRole}'에서 '${adminAccount.role}'로 변경하시겠습니까?\n이는 중요한 보안 변경사항입니다.`
      );
      if (!confirmResult) {
        return;
      }
    }

    // 3. 보안 위험 감지 시 추가 확인
    if (eventCount > 0 && riskLevel === 'HIGH') {
      const confirmResult = window.confirm(
        '입력 데이터에서 보안 위험이 감지되었습니다. 계속 진행하시겠습니까?'
      );
      if (!confirmResult) {
        return;
      }
    }

    setLoading(true);
    try {
      // 4. API 요청 데이터 정화
      const safeAdminData = sanitizeApiRequest({
        id: adminAccount.id,
        pw: adminAccount.pw,
        name: adminAccount.name,
        role: adminAccount.role,
        email: adminAccount.email
      }, '/admin/update');
      
      // 5. 권한 변경 보안 로깅
      if (roleChanged) {
        logSecurityEvent({
          type: 'ADMIN_ROLE_CHANGE',
          field: 'admin_management',
          riskLevel: 'HIGH',
          message: 'Admin role change attempt',
          adminId: safeAdminData.id,
          oldRole: originalRole,
          newRole: safeAdminData.role
        });
      }
      
      // 6. 보안 이벤트 로깅
      if (eventCount > 0) {
        console.warn('Admin update attempt with security events:', {
          eventCount,
          riskLevel,
          adminId: safeAdminData.id,
          roleChanged
        });
      }
      
      const result = await adminAccountService.updateAdmin(adminAccount.idx, safeAdminData);
      
      if (result.success) {
        // 성공 시 보안 로그 정리
        clearSecurityLog();
        openAlert({ title: '성공', description: result.message });
        handleCloseModal();
        fetchAdmins();
      } else {
        openAlert({ title: '알림', description: result.message });
      }
    } catch (e) {
      console.error('Admin update error:', e);
      
      // 오류 시 보안 이벤트 로깅
      logSecurityEvent({
        type: 'ADMIN_UPDATE_ERROR',
        field: 'admin_management',
        riskLevel: 'HIGH',
        message: 'Admin update process error',
        error: e.message
      });
      
      openAlert({ title: '알림', description: '관리자 수정 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * 관리자 데이터 검증
   */
  const validateAdminData = useCallback((isEdit = false) => {
    const newErrors = {};
    
    // ID 검증 (생성 시에만)
    if (!isEdit) {
      if (!adminAccount.id.trim()) {
        newErrors.id = 'ID를 입력해주세요.';
      } else if (adminAccount.id.length < 3) {
        newErrors.id = 'ID는 3자 이상이어야 합니다.';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(adminAccount.id)) {
        newErrors.id = 'ID는 영문, 숫자, _, - 만 사용 가능합니다.';
      }
    }
    
    // 이름 검증
    if (!adminAccount.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    } else if (adminAccount.name.length > 50) {
      newErrors.name = '이름은 50자 이내로 입력해주세요.';
    }
    
    // 이메일 검증
    if (!adminAccount.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminAccount.email)) {
      newErrors.email = '올바른 이메일 형식을 입력해주세요.';
    }
    
    // 역할 검증
    if (!adminAccount.role) {
      newErrors.role = '역할을 선택해주세요.';
    }
    
    // 비밀번호 검증
    if (!isEdit && !adminAccount.pw.trim()) {
      newErrors.pw = '비밀번호를 입력해주세요.';
    } else if (adminAccount.pw && adminAccount.pw.trim()) {
      const passwordValidation = validatePassword(adminAccount.pw);
      if (!passwordValidation.isValid) {
        newErrors.pw = passwordValidation.message;
      }
    }
    
    setValidationErrors(newErrors);
    
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, [adminAccount]);

  /**
   * 안전한 입력 처리 (XSS 방지)
   */
  const handleSafeInput = useCallback((field, value) => {
    let sanitized = value;
    
    // 필드별 정화 방식
    switch (field) {
      case 'id':
        sanitized = sanitizeString(value, 'textOnly');
        break;
      case 'email':
        sanitized = sanitizeString(value, 'email');
        break;
      case 'name':
        sanitized = sanitizeString(value, 'default');
        break;
      default:
        sanitized = sanitizeString(value, 'default');
    }
    
    // XSS 시도 감지
    if (value !== sanitized) {
      logSecurityEvent({
        type: 'XSS_ATTEMPT',
        field,
        riskLevel: 'HIGH',
        message: 'Potential XSS content detected in admin field',
        originalLength: value.length,
        sanitizedLength: sanitized.length
      });
      
      setShowSecurityWarning(true);
    }
    
    // 상태 업데이트
    setAdminAccount(prev => {
      const updated = {
        ...prev,
        [field]: sanitized
      };
      
      // 상태 업데이트 후 즉시 검증 실행
      setTimeout(() => {
        validateAdminDataWithState(updated, prev.isEdit);
      }, 100);
      
      return updated;
    });
  }, [logSecurityEvent]);

  /**
   * 상태를 받아서 검증하는 함수 (실시간 검증용)
   */
  const validateAdminDataWithState = useCallback((accountData, isEdit = false) => {
    const newErrors = {};
    
    // ID 검증 (생성 시에만)
    if (!isEdit) {
      if (!accountData.id.trim()) {
        newErrors.id = 'ID를 입력해주세요.';
      } else if (accountData.id.length < 3) {
        newErrors.id = 'ID는 3자 이상이어야 합니다.';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(accountData.id)) {
        newErrors.id = 'ID는 영문, 숫자, _, - 만 사용 가능합니다.';
      }
    }
    
    // 이름 검증
    if (!accountData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    } else if (accountData.name.length > 50) {
      newErrors.name = '이름은 50자 이내로 입력해주세요.';
    }
    
    // 이메일 검증
    if (!accountData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountData.email)) {
      newErrors.email = '올바른 이메일 형식을 입력해주세요.';
    }
    
    // 역할 검증
    if (!accountData.role) {
      newErrors.role = '역할을 선택해주세요.';
    }
    
    // 비밀번호 검증
    if (!isEdit && !accountData.pw.trim()) {
      newErrors.pw = '비밀번호를 입력해주세요.';
    } else if (accountData.pw && accountData.pw.trim()) {
      const passwordValidation = validatePassword(accountData.pw);
      if (!passwordValidation.isValid) {
        newErrors.pw = passwordValidation.message;
      }
    }
    
    setValidationErrors(newErrors);
    
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, []);

  /**
   * 보안 경고 닫기
   */
  const handleCloseSecurityWarning = () => {
    setShowSecurityWarning(false);
  };

  /**
   * 입력 필드 스타일 (오류 시 강조)
   */
  const getInputClassName = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  return (
    <SectionContainer title="관리자 계정 관리" className="list">
      <Spinner visible={loading} />

      {/* 보안 경고 표시 */}
      {showSecurityWarning && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>⚠️ 보안 경고</strong>
              <p className="mb-0">
                입력 데이터에서 의심스러운 패턴이 감지되어 정화되었습니다.
              </p>
              <small>
                보안 이벤트: {eventCount}건, 위험도: {riskLevel}
              </small>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCloseSecurityWarning}
            >
              확인
            </button>
          </div>
        </div>
      )}
      <div className="search_area">
        <div className="acc_total">
          <h4>
            <b>{admins.length}</b>개 검색되었습니다.
          </h4>
        </div>
        <div className="left">
          <div className="but-flex">
            <button
              className="btn btn-md primary mr-1"
              onClick={handleOpenCreate}
            >
              관리자 등록
            </button>
            <button
              className="btn btn-lg tertiary"
              onClick={handleBulkDeleteConfirm}
            >
              삭제
            </button>
          </div>
        </div>
      </div>
      <div className="content_area">
        <div className="table_area">
          <div className="table">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={handleAllChange}
                    />
                  </th>
                  <th>No</th>
                  <th>ID</th>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>역할</th>
                  <th>상태</th>
                  <th>등록일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin, idx) => (
                  <tr key={admin.idx}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(admin.idx)}
                        onChange={() => handleSelect(admin.idx)}
                      />
                    </td>
                    <td>{idx + 1}</td>
                    <td>{admin.id}</td>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{admin.role}</td>
                    <td>
                      <span
                        className={`badge ${
                          admin.status === 1
                            ? "badge-success"
                            : "badge-secondary"
                        }`}
                      >
                        {admin.status === 1 ? "활성" : "비활성"}
                      </span>
                    </td>
                    <td>{admin.reg_date}</td>
                    <td>
                      <div className="btn-group">
                        <input
                          type="button"
                          className="btn fourbtn mr-1"
                          onClick={() => handleOpenEdit(admin)}
                          value="수정하기"
                        />
                        {admin.status === 1 ? (
                          <input
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={() => handleToggleStatus(admin)}
                            value="비활성화"
                          />
                        ) : (
                          <input
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={() => handleToggleStatus(admin)}
                            value="활성화"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* 등록/수정 모달 */}
      <Modal
        title={adminAccount.isEdit ? "관리자 수정" : "관리자 등록"}
        className="terminal-info mt-2"
        modalClassName="modal-ml"
        confirmLabel={adminAccount.isEdit ? "수정" : "등록"}
        visible={showModal}
        onConfirm={adminAccount.isEdit ? handleUpdateAdmin : handleCreateAdmin}
        onClose={handleCloseModal}
      >
        <dl>
          <dt>ID</dt>
          <dd>
            <input
              type="text"
              value={adminAccount.id}
              onChange={(e) => handleSafeInput("id", e.target.value)}
              disabled={adminAccount.isEdit}
              className={getInputClassName("id")}
              maxLength={50}
            />
            {validationErrors.id && (
              <div className="text-danger mt-1 small">
                {validationErrors.id}
              </div>
            )}
          </dd>
        </dl>
        <dl>
          <dt>이름</dt>
          <dd>
            <input
              type="text"
              value={adminAccount.name}
              onChange={(e) => handleSafeInput("name", e.target.value)}
              className={getInputClassName("name")}
              maxLength={50}
            />
            {validationErrors.name && (
              <div className="text-danger mt-1 small">
                {validationErrors.name}
              </div>
            )}
          </dd>
        </dl>
        <dl>
          <dt>이메일</dt>
          <dd>
            <input
              type="email"
              value={adminAccount.email}
              onChange={(e) => handleSafeInput("email", e.target.value)}
              className={getInputClassName("email")}
              maxLength={100}
            />
            {validationErrors.email && (
              <div className="text-danger mt-1 small">
                {validationErrors.email}
              </div>
            )}
          </dd>
        </dl>
        <dl>
          <dt>역할</dt>
          <dd>
            <select
              value={adminAccount.role}
              onChange={(e) => {
                const newRole = e.target.value;

                // 상태 업데이트
                setAdminAccount((prev) => {
                  const updated = { ...prev, role: newRole };

                  // 권한 변경 시 보안 로깅
                  if (prev.isEdit && originalRole && originalRole !== newRole) {
                    logSecurityEvent({
                      type: "ADMIN_ROLE_CHANGE_ATTEMPT",
                      field: "role",
                      riskLevel: "HIGH",
                      message: "Admin role change in progress",
                      adminId: prev.id,
                      oldRole: originalRole,
                      newRole: newRole,
                    });
                  }

                  // 실시간 검증
                  setTimeout(() => {
                    validateAdminDataWithState(updated, prev.isEdit);
                  }, 100);

                  return updated;
                });
              }}
              className={getInputClassName("role")}
            >
              <option value="">선택</option>
              <option value="calibration_operator">보정 담당</option>
              <option value="operator">오퍼레이터</option>
              <option value="manager">매니저</option>
              <option value="admin">관리자</option>
              <option value="super_admin">시스템관리자</option>
            </select>
            {validationErrors.role && (
              <div className="text-danger mt-1 small">
                {validationErrors.role}
              </div>
            )}
            {adminAccount.isEdit &&
              originalRole !== adminAccount.role &&
              adminAccount.role && (
                <div className="text-warning mt-1 small">
                  ⚠️ 권한이 '{originalRole}'에서 '{adminAccount.role}'로
                  변경됩니다.
                </div>
              )}
          </dd>
        </dl>
        <dl>
          <dt>비밀번호</dt>
          <dd>
            <input
              type="password"
              placeholder={
                adminAccount.isEdit
                  ? "비밀번호를 변경하려면 새로 입력해주세요."
                  : "비밀번호를 입력해주세요."
              }
              value={adminAccount.pw}
              onChange={(e) => {
                const newPassword = e.target.value;

                // 상태 업데이트
                setAdminAccount((prev) => {
                  const updated = { ...prev, pw: newPassword };

                  // 비밀번호 검증
                  if (newPassword) {
                    const validation = validatePassword(newPassword);
                    setPasswordError(
                      validation.isValid ? "" : validation.message
                    );
                  } else {
                    setPasswordError("");
                  }

                  // 실시간 검증
                  setTimeout(() => {
                    validateAdminDataWithState(updated, prev.isEdit);
                  }, 100);

                  return updated;
                });
              }}
              className={getInputClassName("pw")}
              maxLength={100}
            />
            {validationErrors.pw && (
              <div className="text-danger mt-1 small">
                {validationErrors.pw}
              </div>
            )}
          </dd>
        </dl>

        {/* 개발 모드에서 보안 상태 표시 */}
        {process.env.NODE_ENV === "development" && eventCount > 0 && (
          <div className="mt-3 p-3 bg-light border rounded">
            <div className="small text-muted">
              <strong>보안 모니터링 상태:</strong>
              <div>
                위험도:{" "}
                <span
                  className={`badge badge-${
                    riskLevel === "HIGH"
                      ? "danger"
                      : riskLevel === "MEDIUM"
                      ? "warning"
                      : "success"
                  }`}
                >
                  {riskLevel}
                </span>
              </div>
              <div>보안 이벤트: {eventCount}건</div>
              {adminAccount.isEdit && originalRole !== adminAccount.role && (
                <div className="text-warning">⚠️ 권한 변경 감지됨</div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </SectionContainer>
  );
};

export default AdminAccounts;
